import _ from "lodash";
import React from "react";

import {
  FingerprintIcon,
  MultipleChoiceIcon,
  PeopleIcon,
  WorkflowIcon,
} from "../components/icons/themes/windows10";
import {
  allDestinationsData,
  DestinationDefinition,
  destinationKeyToSlugMap,
} from "../destinations/destinations";
import { DestinationName } from "../destinations/types";
import { EventSource } from "../eventTypes";
import { eventSourceDisplay, iconForEventSource } from "../util";
import { ALL_DESTINATIONS_VALUE } from "./components/creation/destination-and-event-def-selection";
import {
  PossibleOperationClass,
  TransformationOperationFactory,
} from "./operations";
import {
  LoadedTransformationSet,
  LoadTransformationData,
  TransformationEventType,
  TransformationIntention,
} from "./types";

export class TransformDefinition {
  ID: string | null;
  createdAt: string;
  enabled: boolean;
  operation: PossibleOperationClass;
  priority: number;

  constructor(props: LoadTransformationData) {
    const { ID, createdAt, enabled, priority } = props;

    this.operation = TransformationOperationFactory.create(props);

    this.ID = ID;
    this.createdAt = createdAt;
    this.enabled = enabled;
    this.priority = priority;
  }

  operationIcon() {
    return this.operation.icon();
  }

  perform(name: string, properties = {}) {
    return this.operation.perform(name, properties);
  }

  operationShortDisplay() {
    return this.operation.shortName();
  }

  exampleResultDisplay(parentSet: TransformDefinitionSet) {
    return this.operation.exampleResultDisplay(parentSet.eventDefName);
  }
}

export type UntransformedEvent = {
  eventName: string;
  eventProperties: Record<string, any>;
};

export type TransformedEvent = UntransformedEvent & {
  renamedProperties: Record<string, any>;
};

export class TransformDefinitionSet {
  id: string;
  envID: string;
  createdAt: string;
  createdByUserId: string;
  eventDefId: string | null;
  eventDefName: string | null;
  eventDefEventSource: EventSource;
  description: string;
  intention: TransformationIntention;
  eventType: TransformationEventType;
  transformations: TransformDefinition[];
  destinationName: DestinationName | null = null;
  destination: DestinationDefinition | null = null;
  priority: number;

  constructor(loadedTransformationSet: LoadedTransformationSet) {
    const {
      transformations,
      id,
      envID,
      createdAt,
      createdByUserId,
      description,
      destinationName,
      eventDefEventSource,
      eventDefId,
      eventDefName,
      intention,
      eventType,
      priority,
    } = loadedTransformationSet;
    this.transformations = _.map(
      transformations,
      (t) => new TransformDefinition(t),
    );

    this.id = id;
    this.envID = envID;
    this.createdAt = createdAt;
    this.createdByUserId = createdByUserId;
    this.eventDefId = eventDefId;
    this.eventDefName = eventDefName;
    this.eventDefEventSource = eventDefEventSource;
    this.description = description;
    this.intention = intention;
    this.eventType = eventType;
    this.priority = priority;
    this.setDestinationName(
      destinationName === ALL_DESTINATIONS_VALUE ? null : destinationName,
    );
  }

  setDestinationName(destinationName: DestinationName | null) {
    this.destinationName = destinationName;
    if (destinationName) {
      this.destination =
        allDestinationsData[destinationKeyToSlugMap[destinationName]] || null;
      this.destinationName =
        (this.destination?.name as DestinationName) || destinationName;
    } else {
      this.destination = null;
    }
  }

  orderedTransformations(reverse = false) {
    return _.orderBy(
      this.transformations,
      (t) =>
        [
          "replaceName",
          "deleteProperty",
          "castPropertyValue",
          "uppercasePropertyValue",
          "lowercasePropertyValue",
          "replacePropertyKey",
        ].indexOf(t.operation.operationType),
      reverse ? "desc" : "asc",
    );
  }

  operationSummary(): {
    propertyTransformations:
      | {
          count: number;
          sample: TransformDefinition;
        }
      | {
          count: 0;
          sample: null;
        };
    eventTransformations:
      | {
          count: 1;
          sample: TransformDefinition;
        }
      | {
          count: 0;
          sample: null;
        };
  } {
    let propertyTransformations = this.transformations.filter(
      (t) =>
        [
          "deleteProperty",
          "castPropertyValue",
          "uppercasePropertyValue",
          "lowercasePropertyValue",
          "replacePropertyKey",
          "sendBuiltinProperty",
          "sendConstantProperty",
          "sqlTransformation",
          "postgresSQLTransformation",
        ].indexOf(t.operation.operationType) !== -1,
    );

    let eventTransformation = this.eventNameTransformation();

    return {
      // @ts-ignore
      propertyTransformations: {
        count: propertyTransformations.length,
        sample:
          propertyTransformations.length > 0
            ? propertyTransformations[0]
            : null,
      },
      // @ts-ignore
      eventTransformations: {
        count: eventTransformation ? 1 : 0,
        sample: eventTransformation ? eventTransformation : null,
      },
    };
  }

  matchesFilter(
    filterColumn: "destinationName" | "eventDefId",
    // "destinationName" is null for transformations that apply to all
    // destinations, so need to allow null here to enable including those
    filterValue: string | null,
    includeAll: boolean = false,
  ) {
    if (this.transformations.length > 0) {
      return (
        this[filterColumn] === filterValue ||
        (includeAll && this[filterColumn] === null)
      );
    }
    return false;
  }

  icon() {
    switch (this.intention) {
      case "fixBadData":
        return MultipleChoiceIcon;
      case "standardEvent":
        return WorkflowIcon;
      case "identifyEvent":
        return PeopleIcon;
    }
  }

  hasEventDefFilter() {
    return this.eventDefId !== null && this.eventDefName !== null;
  }

  hasDestinationFilter() {
    return this.destinationName !== null;
  }

  destinationIcon() {
    if (this.destination) {
      return this.destination.logoSquare;
    }
  }

  eventSourceIcon(): React.FC<any> | undefined {
    if (this.eventType === "identify") {
      return FingerprintIcon;
    }
    if (this.eventDefEventSource) {
      return iconForEventSource(this.eventDefEventSource);
    }
  }

  eventSourceDisplay() {
    if (this.eventDefEventSource) {
      return eventSourceDisplay(this.eventDefEventSource);
    }
    return "";
  }

  canAddRenameEvent() {
    if (this.eventDefId) {
      return !!_.find(
        this.transformations,
        (t) => t.operation.operationType === "replaceName",
      );
    }
    // "All Events" can add multiple rename events
    return true;
  }

  intentionDisplay() {
    switch (this.intention) {
      case "fixBadData":
        return "Modify Data";
      case "standardEvent":
        return "Standard Mapping";
      case "identifyEvent":
        return "Modify User Data";
    }
  }

  destinationDisplay() {
    return this.destinationName || <em>All Integrations</em>;
  }

  eventDefNameDisplay() {
    if (this.eventType === "identify") {
      return "Identify";
    }
    return this.eventDefName || <em>All Events</em>;
  }

  shouldApply(
    envID: string,
    eventDefId: string | null,
    destinationName: DestinationName | null,
  ) {
    return (
      this.envID === envID &&
      (this.eventDefId == null || this.eventDefId === eventDefId) &&
      (this.destinationName == null || this.destinationName === destinationName)
    );
  }

  eventNameTransformation() {
    return _.chain(this.orderedTransformations())
      .filter((t) => t.operation.operationType === "replaceName")
      .first()
      .value();
  }

  targetEventName() {
    if (this.eventType === "identify") {
      return "$identify";
    }
    if (this.eventDefName) {
      return this.eventDefName;
    }
    const firstTransform = this.eventNameTransformation();
    if (firstTransform) {
      return firstTransform.operation.targetValue;
    }
    return null;
  }

  getSampleInputEvent(): UntransformedEvent {
    let eventName = this.targetEventName() || "add_to_cart";
    let eventProperties = {};
    _.map(
      this.orderedTransformations(),
      (t) => t.operation.exampleTargetedProperty,
    ).forEach((obj) => (eventProperties = _.merge(eventProperties, obj)));
    return {
      eventName,
      eventProperties,
    };
  }

  perform(name: string, properties = {}): TransformedEvent {
    let eventName = name,
      eventProperties = properties,
      mapping;
    let renamedProperties: Record<string, string> = {};

    _.forEach(eventProperties, (v, key) => {
      renamedProperties[key] = key;
    });

    this.orderedTransformations().forEach((transformation) => {
      [eventName, eventProperties, mapping] = transformation.perform(
        eventName,
        eventProperties,
      );
      renamedProperties = _.merge(renamedProperties, mapping);
    });

    return {
      eventName,
      eventProperties,
      renamedProperties,
    };
  }
}
